import { useState } from "react";
import {
  createFileRoute,
  Link,
  useRouter,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ApiError } from "@/lib/api";
import {
  gameQuery,
  meQuery,
  scoreboardQuery,
  type ScoreboardCell,
  type ScoreboardPlayer,
} from "@/lib/queries";
import {
  useAdvance,
  useBack,
  useFinish,
  useSkip,
  useSubmitScore,
  useSubmitScoreWithPhoto,
} from "@/lib/mutations";
import { useGameEvents } from "@/lib/sse";
import { Button, buttonStyles } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { CameraModal } from "@/components/CameraModal";
import { StopBoard } from "@/components/StopBoard";

export const Route = createFileRoute("/games/$id/round/$n")({
  component: RoundPage,
  // No auth guard: players are identified by their per-game cookie,
  // not an mt_user session. Score-submit + conductor mutations are
  // gated server-side.
  loader: async ({ context, params }) => {
    const [game, scoreboard] = await Promise.all([
      context.queryClient.ensureQueryData(gameQuery(params.id)),
      context.queryClient.ensureQueryData(scoreboardQuery(params.id)),
    ]);
    return { game, scoreboard };
  },
});

function RoundPage() {
  const router = useRouter();
  const params = Route.useParams();
  const id = params.id;
  const n = Number.parseInt(params.n, 10);

  useGameEvents(id);

  const meQ = useQuery(meQuery);
  const gameQ = useQuery(gameQuery(id));
  const sbQ = useQuery(scoreboardQuery(id));

  const submit = useSubmitScore(id, n);
  const submitWithPhoto = useSubmitScoreWithPhoto(id, n);
  const advance = useAdvance(id);
  const back = useBack(id);
  const skip = useSkip(id);
  const finish = useFinish(id);

  const [editing, setEditing] = useState(false);
  const [scoreInput, setScoreInput] = useState("");
  const [confirm, setConfirm] = useState<null | "skip" | "finish">(null);
  const [cameraOpen, setCameraOpen] = useState(false);

  if (!meQ.data || !gameQ.data || !sbQ.data) return null;

  const me = meQ.data;
  const game = gameQ.data;
  const sb = sbQ.data;

  const round = sb.rounds.find((r) => r.number === n);
  if (!round) {
    return (
      <NotFoundCard message={`Round ${n} doesn't exist in this game.`} />
    );
  }

  // My player id for this game (from meQuery's per-game player records).
  const myPlayerId =
    me.players.find((p) => p.gameId === id)?.id ?? null;

  const myPlayer: ScoreboardPlayer | null = myPlayerId
    ? sb.players.find((p) => p.id === myPlayerId) ?? null
    : null;

  const myCell: ScoreboardCell | null = myPlayerId
    ? round.cells.find((c) => c.playerId === myPlayerId) ?? null
    : null;

  const isHost = myPlayer?.isHost ?? false;
  const isCurrentRound = n === game.currentRound;

  const allSubmitted = round.cells.every((c) => c.submitted);
  const waitingCount = round.cells.filter((c) => !c.submitted).length;

  async function onSubmitScore(e: React.FormEvent) {
    e.preventDefault();
    if (submit.isPending) return;
    const total = Number.parseInt(scoreInput, 10);
    if (!Number.isFinite(total) || total < 0) return;
    try {
      await submit.mutateAsync({ total });
      setEditing(false);
      setScoreInput("");
    } catch {
      // surfaced via submit.error
    }
  }

  async function onAdvance() {
    if (advance.isPending) return;
    try {
      const next = await advance.mutateAsync();
      if (next.status === "finished") {
        await router.navigate({
          to: "/games/$id/scoreboard",
          params: { id },
          search: { finished: 1 },
        });
      } else {
        await router.navigate({
          to: "/games/$id/round/$n",
          params: { id, n: String(next.currentRound) },
        });
      }
    } catch {
      // surfaced below
    }
  }

  async function onBack() {
    if (back.isPending || game.currentRound <= 1) return;
    try {
      const prev = await back.mutateAsync();
      await router.navigate({
        to: "/games/$id/round/$n",
        params: { id, n: String(prev.currentRound) },
      });
    } catch {
      // surfaced below
    }
  }

  async function onSkip() {
    if (skip.isPending) return;
    try {
      const next = await skip.mutateAsync();
      setConfirm(null);
      if (next.status === "finished") {
        await router.navigate({
          to: "/games/$id/scoreboard",
          params: { id },
          search: { finished: 1 },
        });
      } else {
        await router.navigate({
          to: "/games/$id/round/$n",
          params: { id, n: String(next.currentRound) },
        });
      }
    } catch {
      // surfaced below
    }
  }

  async function onFinish() {
    if (finish.isPending) return;
    try {
      await finish.mutateAsync();
      setConfirm(null);
      await router.navigate({
        to: "/games/$id/scoreboard",
        params: { id },
        search: { finished: 1 },
      });
    } catch {
      // surfaced below
    }
  }

  const conductorError = [advance.error, back.error, skip.error, finish.error]
    .filter(Boolean)
    .map((err) => (err instanceof ApiError ? err.message : "Conductor jammed."))
    .at(0);

  const submitError =
    submit.error instanceof ApiError
      ? submit.error.message
      : submit.error
      ? "Couldn't save that score."
      : null;

  return (
    <section className="mx-auto mt-4 max-w-2xl space-y-5 pb-40 md:pb-6">
      <RoundHeader
        n={n}
        totalRounds={sb.rounds.length}
        doubleBlankPenalty={game.rules.doubleBlankPenalty}
        gameId={id}
        isCurrentRound={isCurrentRound}
      />

      {/* Live reveal of the stop's submissions — replaces the old chip grid. */}
      {isCurrentRound && <StopBoard gameId={id} />}

      {/* My-score card (if I'm a player) */}
      {myPlayer && (
        <Card>
          <CardHeader>
            <CardTitle>Your tally</CardTitle>
            <p className="font-mono text-xs text-muted-foreground">
              Sum the pips on your leftover tiles. If you emptied your hand,
              enter 0.
            </p>
          </CardHeader>
          <CardContent>
            {myCell?.submitted && !editing ? (
              <div className="flex items-baseline justify-between gap-3">
                <div>
                  <div className="font-display text-3xl text-foreground">
                    {myCell.total ?? 0}
                  </div>
                  <div className="font-mono text-xs text-muted-foreground">
                    saved · stop {n}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setScoreInput(String(myCell.total ?? 0));
                    setEditing(true);
                  }}
                  disabled={!isCurrentRound}
                >
                  Edit
                </Button>
              </div>
            ) : (
              <form
                id="submit-score"
                onSubmit={onSubmitScore}
                className="space-y-3"
              >
                <Label htmlFor="score-input">Your pip total</Label>
                <Input
                  id="score-input"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  step={1}
                  required
                  autoFocus={editing}
                  value={scoreInput}
                  onChange={(e) => setScoreInput(e.target.value)}
                  className="h-14 text-3xl font-display"
                  placeholder="0"
                />
                {submitError && (
                  <div
                    role="alert"
                    className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 font-mono text-sm text-destructive"
                  >
                    {submitError}
                  </div>
                )}
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-end">
                  <Button
                    type="button"
                    variant="secondary"
                    size="default"
                    onClick={() => setCameraOpen(true)}
                    disabled={!isCurrentRound}
                  >
                    📷 Scan tiles
                  </Button>
                  <div className="hidden md:block">
                    <Button
                      type="submit"
                      form="submit-score"
                      size="lg"
                      disabled={submit.isPending || scoreInput.trim() === ""}
                    >
                      {submit.isPending ? "Saving…" : "Save"}
                    </Button>
                  </div>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      )}

      {/* Host conductor card */}
      {isHost && isCurrentRound && (
        <Card>
          <CardHeader>
            <CardTitle>Conductor</CardTitle>
            <p className="font-mono text-xs text-muted-foreground">
              {allSubmitted
                ? "All in. Punch the next stop."
                : `Waiting on the boys: ${waitingCount}`}
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={onBack}
                disabled={back.isPending || game.currentRound <= 1}
              >
                {back.isPending ? "Backing…" : "Back one stop"}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setConfirm("skip")}
                disabled={skip.isPending}
              >
                Skip this stop
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setConfirm("finish")}
                disabled={finish.isPending}
              >
                Finish early
              </Button>
            </div>
            {conductorError && (
              <div
                role="alert"
                className="mt-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 font-mono text-sm text-destructive"
              >
                {conductorError}
              </div>
            )}
            <div className="mt-4 hidden md:flex md:justify-end">
              <Button
                size="lg"
                onClick={onAdvance}
                disabled={!allSubmitted || advance.isPending}
              >
                {advance.isPending
                  ? "Whistling…"
                  : allSubmitted
                  ? "Next stop"
                  : `Waiting on the boys: ${waitingCount}`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!isHost && isCurrentRound && (
        <p className="text-center font-mono text-sm text-muted-foreground">
          {allSubmitted
            ? "Everyone's in. The host will punch the next stop."
            : "When the boys are all in, the host moves us along."}
        </p>
      )}

      {/* Mobile pinned-bottom CTA — submit-score for player or next-stop for
          host (host's takes priority when all-submitted on the current round). */}
      <MobileBottomCta
        canSubmit={
          !!myPlayer && isCurrentRound && (editing || !myCell?.submitted)
        }
        submitting={submit.isPending}
        submitDisabled={submit.isPending || scoreInput.trim() === ""}
        canAdvance={isHost && isCurrentRound}
        allSubmitted={allSubmitted}
        waitingCount={waitingCount}
        advancing={advance.isPending}
        onAdvance={onAdvance}
      />

      <ConfirmDialog
        open={confirm === "skip"}
        title="Skip this stop?"
        body={`Everyone scores 0 for stop ${n} and we move along. The original tallies are gone.`}
        confirmLabel="Skip the stop"
        destructive
        pending={skip.isPending}
        onConfirm={onSkip}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === "finish"}
        title="Finish early?"
        body="The game ends here and the lowest cumulative total wins. Future stops vanish."
        confirmLabel="Finish the game"
        destructive
        pending={finish.isPending}
        onConfirm={onFinish}
        onCancel={() => setConfirm(null)}
      />

      <CameraModal
        open={cameraOpen}
        gameId={id}
        roundN={n}
        onClose={() => setCameraOpen(false)}
        onSubmitted={() => {
          setCameraOpen(false);
          setEditing(false);
          setScoreInput("");
        }}
        onSubmit={(input) =>
          submitWithPhoto.mutateAsync({
            total: input.total,
            breakdown: input.breakdown,
            photo: input.photo,
          }).then(() => undefined)
        }
      />
    </section>
  );
}

interface RoundHeaderProps {
  n: number;
  totalRounds: number;
  doubleBlankPenalty: boolean;
  gameId: string;
  isCurrentRound: boolean;
}

function RoundHeader({
  n,
  totalRounds,
  doubleBlankPenalty,
  gameId,
  isCurrentRound,
}: RoundHeaderProps) {
  return (
    <header className="space-y-1 px-1">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-3xl text-locomotive-900">
          Stop {n} <span className="text-muted-foreground">of {totalRounds}</span>
        </h1>
        <Link
          to="/games/$id/scoreboard"
          params={{ id: gameId }}
          className={buttonStyles("ghost", "sm")}
        >
          Scoreboard
        </Link>
      </div>
      <p className="font-mono text-xs text-muted-foreground">
        {doubleBlankPenalty ? "double-blank counts 50" : ""}
        {!isCurrentRound ? (doubleBlankPenalty ? " · " : "") + "past stop, read-only" : ""}
      </p>
    </header>
  );
}

interface MobileBottomCtaProps {
  canSubmit: boolean;
  submitting: boolean;
  submitDisabled: boolean;
  canAdvance: boolean;
  allSubmitted: boolean;
  waitingCount: number;
  advancing: boolean;
  onAdvance: () => void;
}

function MobileBottomCta({
  canSubmit,
  submitting,
  submitDisabled,
  canAdvance,
  allSubmitted,
  waitingCount,
  advancing,
  onAdvance,
}: MobileBottomCtaProps) {
  // Two stacked bands so host can both submit (when host is also a player)
  // and advance. The advance band sits above the submit band so finger
  // muscle-memory for "primary action" lands on the right thing.
  return (
    <div className="fixed inset-x-0 bottom-0 z-10 flex flex-col gap-2 border-t border-border/50 bg-sand-50/95 px-4 py-3 backdrop-blur md:hidden">
      {canAdvance && (
        <Button
          size="lg"
          className="w-full"
          onClick={onAdvance}
          disabled={!allSubmitted || advancing}
        >
          {advancing
            ? "Whistling…"
            : allSubmitted
            ? "Next stop"
            : `Waiting on the boys: ${waitingCount}`}
        </Button>
      )}
      {canSubmit && (
        <Button
          type="submit"
          form="submit-score"
          size="lg"
          className="w-full"
          disabled={submitDisabled}
        >
          {submitting ? "Saving…" : "Save"}
        </Button>
      )}
    </div>
  );
}

function NotFoundCard({ message }: { message: string }) {
  return (
    <section className="mx-auto mt-12 max-w-md">
      <Card>
        <CardHeader>
          <CardTitle>Wrong stop</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="font-mono text-sm text-muted-foreground">{message}</p>
        </CardContent>
      </Card>
    </section>
  );
}
